import { directPowerDisplayAs } from "@arenko-group/arenko-ui/dist/enums";
import { capitalize } from "lodash";
import type { DisplayAsObject } from "@/types/data/redux.types";
import type {
  DirectPowerDisplayAsKey,
  Profile,
} from "@/types/data/schedule.types";

export const getDisplayString = (
  value: string,
  mappingObject: DisplayAsObject[]
): string => {
  const displayAs = mappingObject.find((el) => el.name === value)?.display_as;
  if (displayAs) {
    return displayAs;
  }
  return (
    directPowerDisplayAs[value as DirectPowerDisplayAsKey] || capitalize(value)
  );
};

export const getUniqueIdFromAbsolutes = (id: string, absolutes: Profile[]) => {
  const absoluteIndexToPickFrom =
    absolutes.length > 1 ? absolutes.length - 2 : 0;
  return `${id}-${absolutes[absoluteIndexToPickFrom]?.value}`;
};

/**
 * Determines the render state for a component based on loading, error, and data conditions.
 *
 * @param isLoading - Indicates if the data is currently loading.
 * @param isError - Indicates if an error has occurred during data fetching.
 * @param data - The data array to be rendered, or null/undefined if not available.
 * @param hasRenderableData - Optional flag to indicate if the data is renderable (for complex structures).
 * @returns The render state: "loading", "error", "nodata", or "success".
 *
 * @example ```tsx
 * <Foo>
 *   {renderState === "error" && <p>Some error</p>}
 *   {renderState === "nodata" && <p>No data</p>}
 *   {renderState === "success" && <p>Some result</p>}
 * </Foo>
 * ```
 */
export const getRenderState = (
  isLoading: unknown,
  isError: unknown,
  data: unknown[] | null | undefined,
  hasRenderableData?: boolean,
  checkForNullRows = false
): "loading" | "error" | "nodata" | "success" => {
  if (isLoading) return "loading";
  if (isError) return "error";

  if (!data) {
    // NP-554 This should not normally occur as `isLoading` should be true
    return "loading";
  }

  if (data?.length === 0) {
    return "nodata";
  }

  if (checkForNullRows) {
    // if every object in `data` has at least one null value → nodata
    const everyRowHasNull = data.every((row) => {
      if (!row || typeof row !== "object") return false;

      return Object.values(row as Record<string, unknown>).some(
        (value) => value === null
      );
    });

    if (everyRowHasNull) {
      return "nodata";
    }
  }

  // Still loading if no renderable data
  if (hasRenderableData === false) {
    return "loading";
  }

  return "success";
};

/**
 * Returns true if the mouse event occurs within the element.
 */
export const isMouseEventInElement = (e: MouseEvent, element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top <= e.clientY &&
    e.clientY <= rect.top + rect.height &&
    rect.left <= e.clientX &&
    e.clientX <= rect.left + rect.width
  );
};

export const escapeHtml = (input: string) =>
  input
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
